import React, { FC, Fragment } from "react";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
  IbanElement,
} from "@stripe/react-stripe-js";
import { loadStripe, StripeCardElement } from "@stripe/stripe-js";
import { Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";

import FormManualBankPayment from "@/shared/components/organisms/FormManualBankPayment";
import { FormCardPayment } from "@/shared/components/organisms/FormCardPayment";
import { FormBankPayment } from "@/shared/components/organisms/FormBankPayment";
import FormManualPayment from "@/shared/components/organisms/FormManualPayment";
import InputRadio from "@/shared/components/atoms/InputRadio";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faUniversity,
  faHandHoldingUsd,
  faMoneyCheckAlt,
} from "@fortawesome/pro-regular-svg-icons";

export interface User {
  email: string;
  name: string;
}

export interface PaymentFormProps {
  user: User;
  clientSecret: string;
  isLoading: boolean;
  publicKey: string;
  gateway: string;
  selectMethod: (method: string) => void;
  selectManualType: (type: string) => void;
  handleSepaDebitSetup: (values: any) => void;
}

export interface InnerPaymentFormProps {
  clientSecret: string;
  isLoading: boolean;
  gateway: string;
  user: User;
  selectMethod: (method: string) => void;
  selectManualType: (type: string) => void;
  handleSepaDebitSetup: (values: any) => void;
}

export const InnerPaymentForm: FC<InnerPaymentFormProps> = ({
  clientSecret,
  isLoading,
  gateway,
  user,
  selectMethod,
  selectManualType,
  handleSepaDebitSetup,
}) => {
  const [t] = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  return (
    <Formik
      initialValues={{
        email: "",
        name: "",
        hacienda: "",
        paymentMethod: "",
        address: "",
        country: "",
      }}
      onSubmit={async (values, { setSubmitting }: FormikHelpers<any>) => {
        if (values.paymentMethod === "sepa_debit") {
          handleSepaDebitSetup(values);
        } else {
          const setupIntentResult = await stripe?.confirmCardSetup(
            clientSecret,
            {
              payment_method: {
                card: elements?.getElement(CardElement) as StripeCardElement,
                billing_details: {
                  email: values.email,
                  name: values.name,
                },
              },
            }
          );

          if (setupIntentResult?.error) {
            console.error(setupIntentResult.error.message);
          } else {
            console.log(
              "SetupIntent succeeded:",
              setupIntentResult?.setupIntent
            );
          }
        }

        setSubmitting(false);
      }}
    >
      {({
        handleChange,
        handleSubmit,
        setFieldValue,
        isSubmitting,
        values,
        errors,
        touched,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="md:mx-5 my-5">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              {t("store.views.method_payment")}
            </h2>
            <div
              role="group"
              aria-labelledby="paymentMethod"
              className="flex flex-col space-y-2 ml-2 mt-2"
            >
              <label className="flex items-center">
                <InputRadio
                  id=""
                  checked={values.paymentMethod === "1"}
                  key={1}
                  value="1"
                  name="paymentMethod"
                  onChange={handleChange}
                />
                <FontAwesomeIcon icon={faCreditCard} className="ml-2" />
                <span className="ml-2">{t("store.views.card")}</span>
              </label>
              <label className="flex items-center">
                <InputRadio
                  id=""
                  checked={values.paymentMethod === "sepa_debit"}
                  key={2}
                  value="sepa_debit"
                  name="paymentMethod"
                  onChange={() => {
                    setFieldValue("paymentMethod", "sepa_debit");
                    selectMethod("sepa_debit");
                  }}
                />
                <FontAwesomeIcon icon={faUniversity} className="ml-2" />
                <span className="ml-2">{t("store.views.direct_debit")}</span>
              </label>
              <label className="flex items-center">
                <InputRadio
                  id=""
                  checked={values.paymentMethod === "manual"}
                  key={3}
                  value="manual"
                  name="paymentMethod"
                  onChange={() => {
                    setFieldValue("paymentMethod", "manual");
                    selectMethod("manual");
                  }}
                />
                <FontAwesomeIcon icon={faHandHoldingUsd} className="ml-2" />
                <span className="ml-2">{t("store.views.manual")}</span>
              </label>
              <label className="flex items-center">
                <InputRadio
                  id=""
                  checked={values.paymentMethod === "4"}
                  key={4}
                  value="4"
                  name="paymentMethod"
                  onChange={handleChange}
                />
                <FontAwesomeIcon icon={faMoneyCheckAlt} className="ml-2" />
                <span className="ml-2">
                  {t("store.views.manual_direct_debit")}
                </span>
              </label>
            </div>
            <div className="border-b border-gray-300 my-3"></div>
            <div className="mt-4">
              {values.paymentMethod === "1" && (
                <FormCardPayment
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              )}

              {values.paymentMethod === "sepa_debit" && (
                <FormBankPayment
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  values={values}
                  errors={errors}
                  touched={touched}
                  stripe={stripe}
                  elements={elements}
                  publicKey={""}
                  clientSecret={""}
                />
              )}

              {values.paymentMethod === "manual" && (
                <FormManualPayment
                  handleChange={(name, value) => {
                    setFieldValue(name, value);
                    selectManualType(value);
                  }}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              )}

              {values.paymentMethod === "4" && (
                <FormManualBankPayment
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export const PaymentForm: FC<PaymentFormProps> = ({
  user,
  clientSecret,
  isLoading,
  publicKey,
  gateway,
  selectMethod,
  selectManualType,
  handleSepaDebitSetup,
}) => {
  return isLoading ? (
    <Fragment />
  ) : (
    <Elements
      stripe={loadStripe(publicKey)}
      options={{ clientSecret: clientSecret }}
    >
      <InnerPaymentForm
        user={user}
        isLoading={isLoading}
        clientSecret={clientSecret}
        gateway={gateway}
        selectMethod={selectMethod}
        selectManualType={selectManualType}
        handleSepaDebitSetup={handleSepaDebitSetup}
      />
    </Elements>
  );
};
